<template>
  <div class="v2-wishlist-page">
    <!-- Loading -->
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading..."
      />
    </div>

    <template v-else>
      <!-- Header -->
      <div class="v2-flex v2-justify-between v2-items-center v2-mb-4">
        <h1 class="v2-heading-sm">
          Wishlists
        </h1>
        <v2-button
          v-if="isBrand && tabs === 0"
          @click="onClickAddCollection"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19"
            />
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            />
          </svg>
          Add Wishlist
        </v2-button>
      </div>

      <!-- Tabs -->
      <v2-tabs
        :tabs="tabsList"
        :active-tab="activeTabKey"
        @tab-change="onTabChange"
      />

      <!-- Search + Filters -->
      <div
        v-if="showSearchAndFilters"
        class="v2-flex v2-items-center v2-gap-4 v2-mb-4 v2-wishlist-page__filters"
      >
        <div class="v2-wishlist-page__search">
          <svg
            class="v2-wishlist-page__search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <v2-input
            :value="search"
            placeholder="Search"
            @input="search = $event; searchWishlist()"
          />
        </div>

        <v2-reps-filter-dropdown
          v-if="(isEntityAdmin || isSalesManager) && isBrand"
          :options="salesReps"
          :show-self-option="tabs === 0"
          @on-select-options="onFilterBySalesRep"
        />

        <v2-reps-filter-dropdown
          v-if="isEntityAdmin && isBrand"
          :options="salesManagers"
          placeholder="Sales manager"
          :show-self-option="tabs === 0"
          @on-select-options="onFilterBySalesManager"
        />

        <v2-reps-filter-dropdown
          v-if="tabs === 1"
          placeholder="Collection"
          :options="wishlistCollections"
          @on-select-options="onFilterByCollection"
        />
        <v2-reps-filter-dropdown
          v-if="tabs === 1 && isBrand"
          placeholder="Retailer"
          :options="wishlistRetailers"
          @on-select-options="onFilterByRetailer"
        />
      </div>

      <!-- Tab 0: My Wishlists/Selections -->
      <div v-if="tabs === 0">
        <div
          v-if="wishlists.length"
          class="v2-wishlist-page__grid"
        >
          <div
            v-for="wl in wishlists"
            :key="wl._id"
            class="v2-wishlist-page__card-wrapper"
          >
            <v2-wishlist-card
              :wishlist="wl"
              @card-action="onCardAction"
              @fetchWishlists="fetchWishlists"
            />
            <v2-checkbox
              v-if="isBrand"
              class="v2-wishlist-page__select-checkbox"
              :class="{ 'v2-wishlist-page__select-checkbox--visible': isSelectedMultipleSelections }"
              :value="selectedSelections.includes(wl._id)"
              @input="onSelectSelection(wl)"
            />
          </div>
        </div>
        <div
          v-else-if="search || isSearching"
          class="v2-text-center v2-p-8 v2-text-muted"
        >
          No results found
        </div>
        <div
          v-else
          class="v2-text-center v2-p-8"
        >
          <p class="v2-text-muted v2-mb-4">
            You don't have any {{ getWishlistTitle(true) }} yet. Start adding new products from collections.
          </p>
          <v2-button
            v-if="isRetailer"
            variant="outline"
            :disabled="!isEnabledMarketplace"
            @click="$router.push({ name: 'explore' })"
          >
            Go to Collections
          </v2-button>
          <v2-button
            v-if="isBrand"
            @click="onClickAddCollection"
          >
            Add Wishlist
          </v2-button>
        </div>
      </div>

      <!-- Tab 1: Shared By Brands (retailer) / Wishlists with my products (brand) -->
      <div v-if="tabs === 1">
        <!-- Retailer: shared by brands -->
        <div
          v-if="isRetailer && wishlistsBrand.length"
          class="v2-wishlist-page__grid"
        >
          <v2-wishlist-card
            v-for="wl in wishlistsBrand"
            :key="wl._id"
            :wishlist="wl"
            @card-action="onCardAction"
            @fetchWishlists="fetchWishlists"
          />
        </div>
        <!-- Brand: wishlists with my products -->
        <div
          v-else-if="isBrand && wishlistsRetailer.length"
          class="v2-wishlist-page__grid"
        >
          <v2-wishlist-card-for-brand
            v-for="wl in wishlistsRetailer"
            :key="wl._id"
            :wishlist="wl"
            @open-wishlist-notification="openWishlistNotificationModal"
          />
        </div>
        <div
          v-else-if="search || isSearching"
          class="v2-text-center v2-p-8 v2-text-muted"
        >
          No results found
        </div>
        <div
          v-else
          class="v2-text-center v2-p-8 v2-text-muted"
        >
          {{ isRetailer ? 'No brand has shared the wishlist.' : 'No retailer has added your products to their wishlist.' }}
        </div>
      </div>

      <!-- Tab 2: Sales Insights (B2B Merge / iframe context only) -->
      <div v-if="shouldShowSalesInsightsTab && isOnSalesInsightsTab">
        <v2-sales-analytics />
      </div>

      <!-- Multi-select action bar -->
      <div
        v-if="isSelectedMultipleSelections"
        class="v2-wishlist-page__action-bar"
      >
        <div class="v2-wishlist-page__action-bar-center">
          {{ selectedSelections.length }} selection{{ selectedSelections.length !== 1 ? 's' : '' }} selected
        </div>
        <div class="v2-wishlist-page__action-bar-right">
          <v2-button
            variant="outline"
            class="v2-wishlist-page__action-bar-btn"
            @click="onShareSelected"
          >
            Share
          </v2-button>
          <div class="v2-wishlist-page__other-actions">
            <v2-button
              variant="outline"
              class="v2-wishlist-page__action-bar-btn"
              @click="showOtherActionsMenu = !showOtherActionsMenu"
            >
              Other actions
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="v2-ml-1"
              >
                <circle
                  cx="12"
                  cy="5"
                  r="1"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="1"
                />
                <circle
                  cx="12"
                  cy="19"
                  r="1"
                />
              </svg>
            </v2-button>
            <div
              v-if="showOtherActionsMenu"
              class="v2-wishlist-page__other-actions-menu"
            >
              <button @click="selectAllSelections(); showOtherActionsMenu = false">
                Select All
              </button>
              <button @click="resetSelectedSelections(); showOtherActionsMenu = false">
                Deselect All
              </button>
            </div>
          </div>
          <button
            class="v2-wishlist-page__action-bar-close"
            @click="resetSelectedSelections()"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Multi-select share action (modals only) -->
      <v2-wishlist-action
        v-if="isSelectedMultipleSelections"
        ref="multiSelectAction"
        :wishlist="{}"
        :is-multiple-selections="true"
        @fetchWishlists="fetchWishlists"
        @on-open-errors-popup="showShareErrorsModal"
      />

      <!-- Single wishlist action (triggered from card dropdown) -->
      <v2-wishlist-action
        ref="singleAction"
        :wishlist="actionWishlist"
        @fetchWishlists="fetchWishlists"
        @open-referral-model="openReferralModelModal"
      />

      <!-- Create Selection Modal -->
      <v2-create-selection-modal
        v-if="isBrand"
        :show="showCreateModal"
        :fetch-wishlists="fetchWishlists"
        @close="showCreateModal = false"
      />

      <!-- Send Notification Modal -->
      <v2-send-wishlist-notification
        v-if="isBrand"
        :show="showNotificationModal"
        :retailer-name="retailerName"
        :retailer-email="retailerEmail"
        @close="showNotificationModal = false"
      />

      <!-- Share Errors Modal -->
      <v2-share-selections-error-modal
        :show="showErrorsModal"
        :messages="shareSelectionResponseMessages"
        @close="showErrorsModal = false"
      />

      <!-- Referral -->
      <referral-model
        :is-selection="true"
        :referral-email="referralEmail"
        :referral-wishlist-id="referralWishlistId"
        :wishlist="selectedWishlist"
      />
    </template>
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { mapState } from 'vuex'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Tabs from '@/components/v2/V2Tabs.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import { useWishlistList } from '@/composables/useWishlistList'
import { isInIframe } from '@/service/iframe-auth.service'
import V2SalesAnalytics from '@/views/v2/sales-analytics/V2SalesAnalytics.vue'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'
import ReferralModel from '../../apps/referral/referralModel.vue'
import V2WishlistCard from './V2WishlistCard.vue'
import V2WishlistCardForBrand from './V2WishlistCardForBrand.vue'
import V2WishlistAction from './V2WishlistAction.vue'
import V2CreateSelectionModal from './V2CreateSelectionModal.vue'
import V2SendWishlistNotification from './V2SendWishlistNotification.vue'
import V2ShareSelectionsErrorModal from './V2ShareSelectionsErrorModal.vue'

const SALES_INSIGHTS_TAB_INDEX = 2
const SALES_INSIGHTS_TAB_QUERY = 'sales-insights'

// Conditionally import if it exists
let V2RepsFilterDropdown
try {
  V2RepsFilterDropdown = require('@/views/v2/connections/V2RepsFilterDropdown.vue').default
} catch (e) {
  V2RepsFilterDropdown = null
}

export default {
  name: 'V2WishlistList',
  components: {
    V2Spinner,
    V2Button,
    V2Tabs,
    V2Input,
    V2Checkbox,
    V2WishlistCard,
    V2WishlistCardForBrand,
    V2WishlistAction,
    V2CreateSelectionModal,
    V2SendWishlistNotification,
    V2ShareSelectionsErrorModal,
    ReferralModel,
    V2SalesAnalytics,
    ...(V2RepsFilterDropdown ? { V2RepsFilterDropdown } : {}),
  },
  mixins: [UserRoleMixin],
  setup() {
    const wl = useWishlistList()
    return { ...wl }
  },
  data() {
    return {
      showCreateModal: false,
      showNotificationModal: false,
      showErrorsModal: false,
      showOtherActionsMenu: false,
      actionWishlist: {},
    }
  },
  computed: {
    ...mapState({
      SalesAnalyticsNavIndicator: state => state.notification.SalesAnalyticsNavIndicator,
    }),
    isMergeContext() {
      return isInIframe()
    },
    shouldShowSalesInsightsTab() {
      return this.isBrand && this.isMergeContext
    },
    isOnSalesInsightsTab() {
      return this.tabs === SALES_INSIGHTS_TAB_INDEX
    },
    showSearchAndFilters() {
      return this.canShowSearch && !this.isOnSalesInsightsTab
    },
    tabsList() {
      const baseTabs = this.isRetailer
        ? [
          { key: '0', label: 'MY WISHLISTS' },
          { key: '1', label: 'SHARED BY BRANDS' },
        ]
        : [
          { key: '0', label: 'MY WISHLISTS' },
          { key: '1', label: 'WISHLISTS WITH MY PRODUCTS' },
        ]

      if (this.shouldShowSalesInsightsTab) {
        baseTabs.push({
          key: String(SALES_INSIGHTS_TAB_INDEX),
          label: 'SALES INSIGHTS',
          indicator: this.SalesAnalyticsNavIndicator,
        })
      }
      return baseTabs
    },
    activeTabKey() {
      return String(this.tabs)
    },
  },
  mounted() {
    this.initWishlistList()
    this.applyTabFromQueryParam()
  },
  destroyed() {
    this.destroyWishlistList()
  },
  methods: {
    onTabChange(key) {
      this.tabs = parseInt(key, 10)
      if (this.tabs === SALES_INSIGHTS_TAB_INDEX) {
        this.resetSelectedSelections()
      }
      this.syncTabQueryParam(this.tabs)
    },
    applyTabFromQueryParam() {
      if (this.shouldShowSalesInsightsTab && this.$route.query.tab === SALES_INSIGHTS_TAB_QUERY) {
        this.tabs = SALES_INSIGHTS_TAB_INDEX
      }
    },
    syncTabQueryParam(val) {
      const currentTabQuery = this.$route.query.tab
      const nextTabQuery = val === SALES_INSIGHTS_TAB_INDEX ? SALES_INSIGHTS_TAB_QUERY : undefined

      if (currentTabQuery === nextTabQuery) return

      const nextQuery = { ...this.$route.query }
      if (nextTabQuery) {
        nextQuery.tab = nextTabQuery
      } else {
        delete nextQuery.tab
      }
      this.$router.replace({ query: nextQuery }).catch(() => {})
    },
    onClickAddCollection() {
      this.showCreateModal = true
      analytics.track(constants.TRACKS.ACTIONS.BRAND_CLICKS_ADD_SELECTION, {
        'Created By': new Date(),
      })
    },
    onCardAction({ action, wishlist }) {
      this.actionWishlist = wishlist
      this.$nextTick(() => {
        if (this.$refs.singleAction) {
          this.$refs.singleAction.openAction(action)
        }
      })
    },
    openReferralModelModal(emailVal, wishlistVal) {
      this.openReferralModel(emailVal, wishlistVal)
      // The referral model uses $bvModal - in V2 we need a different approach
      // For now, use the existing bootstrap modal trigger
      try {
        this.$bvModal?.show('referal-model')
      } catch (e) {
        // Silent fallback
      }
    },
    openWishlistNotificationModal(name, email) {
      this.openWishlistNotification(name, email)
      this.showNotificationModal = true
    },
    showShareErrorsModal(messages) {
      this.openShareSelectionsErrorModal(messages)
      this.showErrorsModal = true
    },
    onShareSelected() {
      if (this.$refs.multiSelectAction) {
        this.$refs.multiSelectAction.openAction('share')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-wishlist-page {
  &__filters {
    flex-wrap: wrap;
  }

  &__search {
    position: relative;
    flex: 0 0 400px;

    ::v-deep .v2-input {
      padding-left: var(--v2-space-8);
    }
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: var(--v2-space-3);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    color: var(--v2-muted-foreground);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: var(--v2-space-4);
  }

  &__card-wrapper {
    position: relative;
  }

  &__select-checkbox {
    position: absolute;
    top: var(--v2-space-2);
    left: var(--v2-space-2);
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 2;

    ::v-deep .v2-checkbox__box {
      width: 1.75rem;
      height: 1.75rem;
    }

    ::v-deep .v2-checkbox__box svg {
      width: 18px;
      height: 18px;
    }

    &--visible {
      opacity: 1;
    }
  }

  &__card-wrapper:hover &__select-checkbox {
    opacity: 1;
  }

  // Multi-select action bar — uses surface-1 (panel recipe) so the bar
  // reads as visibly translucent glass instead of near-opaque white. Same
  // recipe as the bottom multi-select bar in V2CollectionProducts /
  // V2WishlistProducts so all v2 multi-select bars match.
  &__action-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    padding: var(--v2-space-3) var(--v2-space-6);
    background: var(--v2-glass-surface-1);
    backdrop-filter: blur(var(--v2-glass-blur-lg)) saturate(180%);
    border-bottom: 1px solid var(--v2-glass-stroke);
    box-shadow: var(--v2-glass-shadow);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
    min-height: 56px;
  }

  &__action-bar-center {
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
  }

  &__action-bar-right {
    position: absolute;
    right: var(--v2-space-6);
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__action-bar-btn {
    color: var(--v2-foreground) !important;
    border-color: var(--v2-glass-stroke) !important;

    &:hover {
      background: var(--v2-glass-surface-2) !important;
      border-color: var(--v2-glass-stroke-strong) !important;
    }
  }

  &__other-actions {
    position: relative;
  }

  &__other-actions-menu {
    position: absolute;
    top: calc(100% + var(--v2-space-1));
    right: 0;
    min-width: 160px;
    // Floating glass — actions popover; near-opaque white so menu items
    // stay legible if the page mesh shows a strong colour wash behind it.
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-glass-shadow-floating);
    z-index: 210;
    padding: var(--v2-space-1) 0;

    button {
      display: block;
      width: 100%;
      padding: var(--v2-space-2) var(--v2-space-3);
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      background: transparent;
      border: none;
      text-align: left;
      cursor: pointer;
      transition: background 0.1s ease;

      &:hover {
        background: var(--v2-glass-surface-2);
      }
    }
  }

  &__action-bar-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: transparent;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    color: var(--v2-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: var(--v2-glass-surface-2);
      border-color: var(--v2-glass-stroke-strong);
    }
  }
}

.v2-ml-1 { margin-left: var(--v2-space-1); }
.v2-text-center { text-align: center; }
.v2-text-muted { color: var(--v2-muted-foreground); }
</style>
